import React from 'react';
import { Box, Grid, Typography, Paper, Snackbar, Alert, Skeleton } from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

import { PageHeader, DataTableCard, FilterChips, MetricCard, useDesignTokens } from '../ui';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius } from '../../themes/smartinventoryTheme';
import buildMyInvoicesColumns, { dueInDays, DUE_SOON_DAYS } from './buildMyInvoicesColumns';
import useMyInvoicesPage from './useMyInvoicesPage';

const MyInvoices = () => {
  const p = useMyInvoicesPage();
  const { tokens: t } = useDesignTokens();
  const columns = buildMyInvoicesColumns({ t: p.t, i18n: p.i18n, onDownload: p.downloadPdf });

  // Due-date banners. A reminder from the admin supersedes the automatic
  // approaching/overdue message for that invoice, so nothing shows twice.
  const rows = p.effectiveRows || [];
  const remindedInvoices = rows.filter((r) => r.status === 'issued' && r.reminderSentAt);
  const remindedIds = new Set(remindedInvoices.map((r) => r._id));
  const overdueInvoices = rows.filter((r) => {
    const days = dueInDays(r);
    return !remindedIds.has(r._id) && days !== null && days < 0;
  });
  const dueSoonInvoices = rows.filter((r) => {
    const days = dueInDays(r);
    return !remindedIds.has(r._id) && days !== null && days >= 0 && days <= DUE_SOON_DAYS;
  });

  const statusOptions = [
    { value: 'all', label: p.t('All Statuses') },
    { value: 'issued', label: p.t('Issued') },
    { value: 'paid', label: p.t('Paid') },
    { value: 'void', label: p.t('Void') },
  ];

  return (
    <Box>
      <PageHeader
        title={p.t('My Invoices', { defaultValue: 'My Invoices' })}
        subtitle={p.t('{{count}} invoices', { count: p.total, defaultValue: `${p.total} invoices` })}
      />

      {remindedInvoices.map((inv) => (
        <Alert key={`remind-${inv._id}`} severity="info" icon={<NotificationsNoneIcon fontSize="inherit" />} sx={{ mb: 1.5 }}>
          {inv.dueDate
            ? p.t('Friendly reminder: invoice {{number}} is due on {{date}}.', {
                number: inv.invoiceNumber,
                date: new Date(inv.dueDate).toLocaleDateString(),
                defaultValue: `Friendly reminder: invoice ${inv.invoiceNumber} is due on ${new Date(inv.dueDate).toLocaleDateString()}.`,
              })
            : p.t('Friendly reminder: invoice {{number}} is awaiting payment.', {
                number: inv.invoiceNumber,
                defaultValue: `Friendly reminder: invoice ${inv.invoiceNumber} is awaiting payment.`,
              })}
        </Alert>
      ))}
      {overdueInvoices.map((inv) => (
        <Alert key={`overdue-${inv._id}`} severity="error" sx={{ mb: 1.5 }}>
          {p.t('Invoice {{number}} was due on {{date}}.', {
            number: inv.invoiceNumber,
            date: new Date(inv.dueDate).toLocaleDateString(),
            defaultValue: `Invoice ${inv.invoiceNumber} was due on ${new Date(inv.dueDate).toLocaleDateString()}.`,
          })}
        </Alert>
      ))}
      {dueSoonInvoices.map((inv) => (
        <Alert key={`due-${inv._id}`} severity="warning" sx={{ mb: 1.5 }}>
          {p.t('Invoice {{number}} is due on {{date}}.', {
            number: inv.invoiceNumber,
            date: new Date(inv.dueDate).toLocaleDateString(),
            defaultValue: `Invoice ${inv.invoiceNumber} is due on ${new Date(inv.dueDate).toLocaleDateString()}.`,
          })}
        </Alert>
      ))}

      {p.billingStatus === 'loading' ? (
        <Grid container spacing={1.75} sx={{ mb: 2 }}>
          {[0, 1].map((i) => <Grid item xs={6} md={3} key={i}><Skeleton variant="rounded" height={100} sx={{ borderRadius: `${radius.lg}px` }} /></Grid>)}
        </Grid>
      ) : p.billing && (
        <Grid container spacing={1.75} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <MetricCard
              label={p.t('Outstanding', { defaultValue: 'Outstanding' })}
              value={formatCurrency(p.billing.outstandingTotal)}
              delta={p.billing.unpaidCount > 0 ? { direction: 'neutral', text: p.t('{{count}} unpaid', { count: p.billing.unpaidCount, defaultValue: `${p.billing.unpaidCount} unpaid` }) } : undefined}
              tone={p.billing.unpaidCount > 0 ? 'warning' : undefined}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <MetricCard
              label={p.t('Paid this year', { defaultValue: 'Paid this year' })}
              value={formatCurrency(p.billing.paidThisYear)}
              delta={{ direction: 'neutral', text: p.t('{{count}} invoices', { count: p.billing.paidCount, defaultValue: `${p.billing.paidCount} invoices` }) }}
            />
          </Grid>
        </Grid>
      )}

      {p.billing && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{p.t('Spend · last 6 months', { defaultValue: 'Spend · last 6 months' })}</Typography>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={p.billing.monthlySpend} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={t.border} />
              <XAxis dataKey="month" tick={{ fontSize: 10.5, fill: t.muted }} tickFormatter={(v) => new Date(`${v}-01`).toLocaleDateString(undefined, { month: 'short' })} axisLine={{ stroke: t.border }} tickLine={false} />
              <YAxis hide />
              <RechartsTooltip formatter={(value) => formatCurrency(value)} labelFormatter={(v) => new Date(`${v}-01`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })} contentStyle={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: radius.md, fontSize: 12.5 }} />
              <Bar dataKey="value" fill={t.accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      <DataTableCard
        status={p.effectiveStatus}
        rows={p.effectiveRows}
        columns={columns}
        toolbar={(
          <FilterChips options={statusOptions} value={p.statusFilter} onChange={p.setStatusFilter} filtersLabel={p.t('Filters')} />
        )}
        bulkBar={{
          count: p.selection.length,
          countLabel: p.t('{{count}} selected', { count: p.selection.length, defaultValue: `${p.selection.length} selected` }),
          actions: [
            { label: p.t('Download PDFs', { defaultValue: 'Download PDFs' }), onClick: p.downloadSelectedPdfs },
          ],
          onClear: () => p.setSelection([]),
        }}
        empty={{
          kind: 'none',
          icon: RequestQuoteIcon,
          title: p.t('No invoices found.'),
          description: p.t('Invoices addressed to you will show up here.', { defaultValue: 'Invoices addressed to you will show up here.' }),
        }}
        error={{
          title: p.t("Couldn't load invoices", { defaultValue: "Couldn't load invoices" }),
          description: p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' }),
          request: p.errorInfo?.request,
          retryLabel: p.t('Try again', { defaultValue: 'Try again' }),
          onRetry: p.fetchInvoices,
        }}
        permission={{
          title: p.t("You don't have access to Invoices", { defaultValue: "You don't have access to Invoices" }),
          description: p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' }),
        }}
        getRowId={(row) => row._id}
        checkboxSelection
        rowSelectionModel={p.selection}
        onRowSelectionModelChange={p.setSelection}
        loadMore={p.remaining > 0 ? {
          label: p.t('Load {{count}} more · {{remaining}} remaining', {
            count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
          }),
          onClick: p.loadMore,
          loading: p.loadingMore,
        } : undefined}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>{p.snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MyInvoices;
